// Which shops to show, in what order.
//
// Proximity decides the order. Ratings are supporting information — shown, and
// used only to separate branches that are effectively the same distance away.
//
// Two things this deliberately does NOT do:
//
//   * It never multiplies rating by review count. That product is dominated by
//     volume: a 3.9 with 4,000 reviews beats a 4.8 with 200 every time, so the
//     ranking stops being about quality and becomes about how busy a branch is.
//     Review count is used only as a confidence gate — enough reviews to take
//     the average seriously, and after that the average stands on its own.
//
//   * It never claims pickup. Every store here is a nearby grocery shop with a
//     website; whether that branch does online pickup is unverified, and the
//     UI has to say so. Ranking eligibility means "worth showing", not
//     "confirmed to do what you want".
//
// NOT a mock. Real provider results go through exactly this.

import { distanceMeters } from './geo.js';
import { isRecommended } from './recommendedBrands.js';

// Inside this band, two branches are "the same distance away" as far as a
// person choosing between them is concerned, so the better-rated one goes
// first. Outside it, the closer one wins regardless of stars.
export const TIE_BAND_M = 400;

// Below this, an average is one loud opinion rather than a reputation. Such a
// store is not penalised — it simply has no rating to break ties with.
export const MIN_CONFIDENT_REVIEWS = 15;

// A store is worth showing if we can name it, place it, and send the user
// somewhere to check for themselves. No website means no way to check pickup,
// which makes the row a dead end.
//
// Note what this does NOT establish. A website is a floor, not proof of a
// retailer: a petrol brand has a corporate site with no store behind it and
// passes this test comfortably. What keeps forecourts and kitchenware shops out
// of the list is the primary-type filter on the request (placesMapping.js), not
// this function. Do not add reasoning here that the check cannot support.
export function isEligible(store) {
  if (!store) return false;
  if (!store.name || !store.website) return false;
  const { lat, lon } = store.location ?? {};
  return Number.isFinite(lat) && Number.isFinite(lon);
}

// Within one distance band, a full supermarket comes before a smaller or
// specialist grocer — the list is for filling a shopping list, and that is the
// one thing store kind honestly tells us. It is NOT a claim about stock: a
// supermarket that happens not to carry tamarind paste still ranks first, and
// the specialist that does still ranks second. An unknown kind sits between
// them rather than last, since "the provider didn't say" is not evidence.
const KIND_ORDER = { supermarket: 0, grocery: 2 };
export function kindRank(store) {
  return KIND_ORDER[store?.kind] ?? 1;
}

export function confidentRating(store) {
  const rating = store?.rating;
  const reviews = store?.reviewCount ?? 0;
  if (typeof rating !== 'number' || !Number.isFinite(rating)) return null;
  return reviews >= MIN_CONFIDENT_REVIEWS ? rating : null;
}

export function rankStores(stores, origin, { limit = 3 } = {}) {
  if (!origin) return [];

  const measured = (stores ?? [])
    .filter(isEligible)
    .map((store) => ({ ...store, distanceMeters: distanceMeters(origin, store.location) }))
    .filter((store) => typeof store.distanceMeters === 'number');

  measured.sort((a, b) => {
    // Bucketing by band is what keeps proximity primary: rating can only ever
    // reorder stores that landed in the same band.
    const bandA = Math.floor(a.distanceMeters / TIE_BAND_M);
    const bandB = Math.floor(b.distanceMeters / TIE_BAND_M);
    if (bandA !== bandB) return bandA - bandB;

    // Kind before rating: at the same distance, what a shop IS matters more to
    // someone holding a thirteen-item list than half a star does.
    const kindA = kindRank(a);
    const kindB = kindRank(b);
    if (kindA !== kindB) return kindA - kindB;

    // Our own recommendation, and last of the three tiebreakers on purpose.
    // Band and kind are facts about the branch; this is an opinion, so it only
    // gets to speak once the facts have tied. Being inside the band comparison
    // is the whole safety property: a recommended chain six kilometres away can
    // never displace an ordinary supermarket one kilometre away.
    const pickA = isRecommended(a) ? 0 : 1;
    const pickB = isRecommended(b) ? 0 : 1;
    if (pickA !== pickB) return pickA - pickB;

    const ratingA = confidentRating(a);
    const ratingB = confidentRating(b);
    if (ratingA !== ratingB) {
      if (ratingA === null) return 1;
      if (ratingB === null) return -1;
      if (ratingB !== ratingA) return ratingB - ratingA;
    }

    return a.distanceMeters - b.distanceMeters;
  });

  // Fewer than `limit` is a normal result, not a gap to fill. Nothing is
  // padded, substituted or invented to reach three.
  const top = measured.slice(0, limit);

  // One exception, and it is not padding: a shop we recommend that the provider
  // really did return, which the distance cut happened to drop. It is appended
  // rather than promoted, so the first three stay strictly the nearest — the
  // user sees "here are your nearest three, and also this one" rather than a
  // recommendation quietly rearranging the list. `measured` is already sorted,
  // so this is the nearest such branch, and only ever one.
  const rescued = measured.slice(limit).find(isRecommended);
  return rescued ? [...top, rescued] : top;
}
